import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Save, RefreshCw, ChevronDown, ChevronUp, Check, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

// Category labels
const CATEGORY_LABELS = {
  strength: '근력',
  muscular_endurance: '근지구력',
  cardio: '심폐지구력',
};

const EXERCISE_LABELS = {
  deadlift: '데드리프트',
  squat: '스쿼트',
  bench_press: '벤치프레스',
  shoulder_press: '숄더프레스',
  situp: '싯업',
  pushup: '푸시업',
  plank: '플랭크',
  wall_squat: '월 스쿼트',
  barbell_squat_endurance: '바벨 스쿼트',
  barbell_deadlift_endurance: '바벨 데드리프트',
  barbell_shoulder_press_endurance: '바벨 숄더프레스',
  amrap1: 'AMRAP1',
  amrap2: 'AMRAP2',
  amrap3: 'AMRAP3',
  amrap4: 'AMRAP4',
  running: '런닝',
  rowing: '로잉 퍼포먼스',
  vo2: 'VO2max',
};

const AGE_BAND_LABELS = {
  '20': '20대',
  '30': '30대',
  '40': '40대',
  '50': '50대 이상',
};

const GENDER_LABELS = {
  'M': '남성',
  'F': '여성',
};

const THRESHOLD_ORDER = ['VLow', 'Low', 'Avg', 'Good', 'Excellent', 'Superior'];

// Exercises that use a body-weight based reference load for scoring
const BARBELL_ENDURANCE_EXERCISES = new Set([
  'barbell_squat_endurance',
  'barbell_deadlift_endurance',
  'barbell_shoulder_press_endurance',
]);

const ScoringRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRule, setExpandedRule] = useState(null);
  const [editedRules, setEditedRules] = useState({});
  // Per-rule edited base load ratios: { [ruleCode]: { m: number|'', f: number|'' } }
  const [editedRatios, setEditedRatios] = useState({});
  const [saving, setSaving] = useState({});
  
  // Age average scores state
  const [ageAverages, setAgeAverages] = useState({
    strength: '',
    muscular_endurance: '',
    cardio: '',
    body_composition: '',
    balance: ''
  });
  const [ageAveragesLoading, setAgeAveragesLoading] = useState(false);
  const [ageAveragesSaving, setAgeAveragesSaving] = useState(false);

  // Score level thresholds state
  const [scoreLevels, setScoreLevels] = useState([]);
  const [scoreLevelsSaving, setScoreLevelsSaving] = useState(false);

  // Fetch score levels
  const fetchScoreLevels = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/score-levels');
      setScoreLevels(response.data.levels || []);
    } catch (error) {
      console.error('Failed to fetch score levels:', error);
    }
  }, []);

  const updateScoreLevel = (idx, field, value) => {
    setScoreLevels(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        [field]: field === 'min_score' ? (value === '' ? '' : Number(value)) : value,
      };
      return next;
    });
  };

  const addScoreLevel = () => {
    setScoreLevels(prev => [...prev, { min_score: '', name: '' }]);
  };

  const removeScoreLevel = (idx) => {
    setScoreLevels(prev => prev.filter((_, i) => i !== idx));
  };

  const saveScoreLevels = async () => {
    // Client-side validation
    if (scoreLevels.length === 0) {
      toast.error('최소 1개 이상의 레벨이 필요합니다');
      return;
    }
    for (let i = 0; i < scoreLevels.length; i++) {
      const l = scoreLevels[i];
      if (!l.name || !String(l.name).trim()) {
        toast.error(`${i + 1}번째 레벨 이름을 입력해주세요`);
        return;
      }
      if (l.min_score === '' || l.min_score === null || isNaN(Number(l.min_score))) {
        toast.error(`${i + 1}번째 최소 점수를 입력해주세요`);
        return;
      }
    }
    const scores = scoreLevels.map(l => Number(l.min_score));
    if (new Set(scores).size !== scores.length) {
      toast.error('최소 점수가 중복됩니다');
      return;
    }
    const sorted = [...scores].sort((a, b) => a - b);
    if (JSON.stringify(scores) !== JSON.stringify(sorted)) {
      toast.error('최소 점수는 오름차순이어야 합니다');
      return;
    }

    setScoreLevelsSaving(true);
    try {
      await apiClient.put('/admin/score-levels', {
        levels: scoreLevels.map(l => ({
          min_score: Number(l.min_score),
          name: String(l.name).trim(),
        })),
      });
      toast.success('점수 레벨 기준이 저장되었습니다');
      fetchScoreLevels();
    } catch (error) {
      toast.error('저장 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScoreLevelsSaving(false);
    }
  };

  // Fetch age average scores
  const fetchAgeAverages = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/age-average-scores');
      setAgeAverages({
        strength: response.data.strength ?? '',
        muscular_endurance: response.data.muscular_endurance ?? '',
        cardio: response.data.cardio ?? '',
        body_composition: response.data.body_composition ?? '',
        balance: response.data.balance ?? ''
      });
    } catch (error) {
      console.error('Failed to fetch age averages:', error);
    }
  }, []);

  // Save age average scores
  const saveAgeAverages = async () => {
    setAgeAveragesSaving(true);
    try {
      const payload = {
        strength: ageAverages.strength === '' ? null : parseFloat(ageAverages.strength),
        muscular_endurance: ageAverages.muscular_endurance === '' ? null : parseFloat(ageAverages.muscular_endurance),
        cardio: ageAverages.cardio === '' ? null : parseFloat(ageAverages.cardio),
        body_composition: ageAverages.body_composition === '' ? null : parseFloat(ageAverages.body_composition),
        balance: ageAverages.balance === '' ? null : parseFloat(ageAverages.balance)
      };
      await apiClient.put('/admin/age-average-scores', payload);
      toast.success('연령 평균값이 저장되었습니다');
    } catch (error) {
      console.error('Failed to save age averages:', error);
      toast.error('저장 실패');
    } finally {
      setAgeAveragesSaving(false);
    }
  };

  // Fetch all scoring rules
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/scoring-rules');
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      toast.error('기준표 로딩 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchAgeAverages();
    fetchScoreLevels();
  }, [fetchRules, fetchAgeAverages, fetchScoreLevels]);

  // Toggle rule expansion
  const toggleExpand = (ruleCode) => {
    if (expandedRule === ruleCode) {
      setExpandedRule(null);
    } else {
      setExpandedRule(ruleCode);
      // Initialize edited data if not exists
      if (!editedRules[ruleCode]) {
        const rule = rules.find(r => r.rule_code === ruleCode);
        if (rule) {
          setEditedRules(prev => ({
            ...prev,
            [ruleCode]: JSON.parse(JSON.stringify(rule.thresholds))
          }));
        }
      }
    }
  };

  // Update threshold value
  const updateThreshold = (ruleCode, gender, ageBand, level, value) => {
    setEditedRules(prev => {
      const newData = { ...prev };
      if (!newData[ruleCode]) {
        const rule = rules.find(r => r.rule_code === ruleCode);
        newData[ruleCode] = JSON.parse(JSON.stringify(rule.thresholds));
      }
      
      if (!newData[ruleCode][gender]) {
        newData[ruleCode][gender] = {};
      }
      if (!newData[ruleCode][gender][ageBand]) {
        newData[ruleCode][gender][ageBand] = {};
      }
      
      newData[ruleCode][gender][ageBand][level] = value === '' ? 0 : parseFloat(value);
      return newData;
    });
  };

  // Save rule changes
  const saveRule = async (ruleCode) => {
    const rule = rules.find(r => r.rule_code === ruleCode);
    const thresholdsChanged = !!editedRules[ruleCode]
      && JSON.stringify(rule?.thresholds) !== JSON.stringify(editedRules[ruleCode]);
    const ratiosChanged = !!editedRatios[ruleCode];

    if (!thresholdsChanged && !ratiosChanged) {
      toast.info('변경된 내용이 없습니다');
      return;
    }

    setSaving(prev => ({ ...prev, [ruleCode]: true }));

    try {
      const body = {};
      if (thresholdsChanged) body.thresholds = editedRules[ruleCode];
      if (ratiosChanged) {
        const edited = editedRatios[ruleCode];
        if (edited.m !== undefined) {
          const v = edited.m;
          body.base_load_ratio_m = (v === '' || v === null) ? null : parseFloat(v) / 100;
        }
        if (edited.f !== undefined) {
          const v = edited.f;
          body.base_load_ratio_f = (v === '' || v === null) ? null : parseFloat(v) / 100;
        }
      }

      await apiClient.put(`/admin/scoring-rules/${ruleCode}`, body);

      toast.success('기준표가 저장되었습니다');

      // Refresh the rules list
      await fetchRules();

      // Clear edited state for this rule
      setEditedRules(prev => {
        const newData = { ...prev };
        delete newData[ruleCode];
        return newData;
      });
      setEditedRatios(prev => {
        const newData = { ...prev };
        delete newData[ruleCode];
        return newData;
      });
    } catch (error) {
      console.error('Failed to save rule:', error);
      toast.error('저장 실패: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(prev => ({ ...prev, [ruleCode]: false }));
    }
  };

  // Check if rule has unsaved changes
  const hasChanges = (ruleCode) => {
    if (editedRatios[ruleCode]) return true;
    if (!editedRules[ruleCode]) return false;
    const rule = rules.find(r => r.rule_code === ruleCode);
    if (!rule) return false;
    return JSON.stringify(rule.thresholds) !== JSON.stringify(editedRules[ruleCode]);
  };

  // Base load ratio helpers (stored as 0~1 fraction in DB, edited as % in UI)
  const getBaseRatioPct = (rule, side /* 'm' | 'f' */) => {
    const editedSide = editedRatios[rule.rule_code]?.[side];
    if (editedSide !== undefined) return editedSide;
    const dbVal = side === 'm' ? rule.base_load_ratio_m : rule.base_load_ratio_f;
    if (dbVal === undefined || dbVal === null) return '';
    return Math.round(dbVal * 1000) / 10; // e.g., 0.5 -> 50, 0.45 -> 45
  };

  const updateBaseRatio = (ruleCode, side, value) => {
    setEditedRatios(prev => ({
      ...prev,
      [ruleCode]: { ...(prev[ruleCode] || {}), [side]: value }
    }));
  };

  // Get current threshold value (edited or original)
  const getThresholdValue = (ruleCode, gender, ageBand, level) => {
    if (editedRules[ruleCode]?.[gender]?.[ageBand]?.[level] !== undefined) {
      return editedRules[ruleCode][gender][ageBand][level];
    }
    const rule = rules.find(r => r.rule_code === ruleCode);
    return rule?.thresholds?.[gender]?.[ageBand]?.[level] ?? '';
  };

  // Get available threshold levels for a rule
  // - Superior가 score_map에 있으면 6단계 (예: VO2max)
  // - Excellent까지만 있으면 5단계 (예: 근력 3종, 러닝/로잉)
  // - 그 외에는 4단계 (Good까지)
  const getThresholdLevels = (rule) => {
    const sm = rule.score_map || {};
    if ('Superior' in sm) return THRESHOLD_ORDER;            // 6단계
    if ('Excellent' in sm) return THRESHOLD_ORDER.slice(0, 5); // 5단계
    return THRESHOLD_ORDER.slice(0, 4);                       // 4단계
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">점수 기준표 관리</h1>
          <p className="text-gray-500 text-sm mt-1">
            기준값을 수정하면 다음 측정부터 즉시 반영됩니다
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchRules(); fetchAgeAverages(); }} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          새로고침
        </Button>
      </div>

      {/* Age Average Scores Section */}
      <Card className="mb-6 border-blue-200 bg-blue-50/30">
        <CardHeader className="py-4">
          <CardTitle className="text-lg flex items-center gap-2">
            📊 연령 평균값 설정
            <span className="text-sm font-normal text-gray-500">
              (회원 상세 페이지의 레이더 차트에서 비교용으로 표시됩니다)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { key: 'strength', label: '근력' },
              { key: 'muscular_endurance', label: '근지구력' },
              { key: 'cardio', label: '심폐지구력' },
              { key: 'body_composition', label: '신체 조성' },
              { key: 'balance', label: '신체 균형' }
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <label className="text-sm font-medium text-gray-700">{label}</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="미설정"
                  value={ageAverages[key]}
                  onChange={(e) => setAgeAverages(prev => ({ ...prev, [key]: e.target.value }))}
                  className="bg-white"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <Button 
              onClick={saveAgeAverages} 
              disabled={ageAveragesSaving}
              className="gap-2"
            >
              {ageAveragesSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              연령 평균값 저장
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            * 값을 비워두면 해당 항목의 평균선이 레이더 차트에 표시되지 않습니다.
          </p>
        </CardContent>
      </Card>

      {/* Score Level Thresholds Section */}
      <Card className="mb-6 border-purple-200 bg-purple-50/30" data-testid="score-levels-card">
        <CardHeader className="py-4">
          <CardTitle className="text-lg flex items-center gap-2">
            🏆 점수 레벨 기준 설정
            <span className="text-sm font-normal text-gray-500">
              (회원 앱의 점수 화면에서 표시되는 레벨 이름/구간을 관리합니다)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className="text-left p-2 font-medium w-20">순서</th>
                  <th className="text-left p-2 font-medium w-32">최소 점수</th>
                  <th className="text-left p-2 font-medium">레벨 이름</th>
                  <th className="text-left p-2 font-medium">점수 구간</th>
                  <th className="p-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {scoreLevels.map((level, idx) => {
                  const next = scoreLevels[idx + 1];
                  const rangeText = next
                    ? `${level.min_score === '' ? '?' : level.min_score} ~ ${Number(next.min_score) - 1}`
                    : `${level.min_score === '' ? '?' : level.min_score} 이상`;
                  return (
                    <tr key={idx} className="border-b">
                      <td className="p-2 text-gray-500">{idx + 1}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={level.min_score}
                          onChange={(e) => updateScoreLevel(idx, 'min_score', e.target.value)}
                          className="w-24 bg-white"
                          data-testid={`score-level-min-${idx}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="text"
                          value={level.name}
                          onChange={(e) => updateScoreLevel(idx, 'name', e.target.value)}
                          placeholder="레벨 이름"
                          className="max-w-48 bg-white"
                          data-testid={`score-level-name-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-gray-600 text-xs">{rangeText}</td>
                      <td className="p-2">
                        {scoreLevels.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => removeScoreLevel(idx)}
                            data-testid={`score-level-remove-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={addScoreLevel}
              className="gap-1"
              data-testid="score-level-add-btn"
            >
              <Plus className="h-4 w-4" />
              레벨 추가
            </Button>
            <Button
              onClick={saveScoreLevels}
              disabled={scoreLevelsSaving}
              className="gap-2"
              data-testid="score-levels-save-btn"
            >
              {scoreLevelsSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              레벨 기준 저장
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            * 각 레벨의 최소 점수를 오름차순으로 입력하세요. 다음 레벨의 최소 점수 직전까지 해당 레벨로 표시됩니다.
          </p>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {rules.map((rule) => {
          const isExpanded = expandedRule === rule.rule_code;
          const thresholdLevels = getThresholdLevels(rule);
          const changed = hasChanges(rule.rule_code);
          
          return (
            <Card key={rule.rule_code} className={changed ? 'border-orange-300' : ''}>
              <CardHeader 
                className="cursor-pointer py-4"
                onClick={() => toggleExpand(rule.rule_code)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      rule.category === 'strength' ? 'bg-red-100 text-red-800' :
                      rule.category === 'muscular_endurance' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {CATEGORY_LABELS[rule.category] || rule.category}
                    </span>
                    <CardTitle className="text-lg">
                      {EXERCISE_LABELS[rule.exercise] || rule.exercise}
                    </CardTitle>
                    <span className="text-sm text-gray-500">
                      ({rule.rule_code})
                    </span>
                    {changed && (
                      <span className="text-xs text-orange-600 font-medium">
                        • 수정됨
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isExpanded && (
                      <Button 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          saveRule(rule.rule_code);
                        }}
                        disabled={saving[rule.rule_code] || !changed}
                        className="gap-1"
                      >
                        {saving[rule.rule_code] ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        저장
                      </Button>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="text-sm text-gray-600 mb-4">
                    {rule.description}
                  </div>

                  {/* Base load ratio editor (barbell endurance only) */}
                  {BARBELL_ENDURANCE_EXERCISES.has(rule.exercise) && (
                    <div
                      className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
                      data-testid={`base-load-ratio-${rule.exercise}`}
                    >
                      <div className="text-sm font-medium mb-1">
                        🏋️ 기준중량 (체중 대비 %)
                      </div>
                      <div className="text-xs text-gray-600 mb-3">
                        회원 체중에 아래 비율을 곱해 개인별 기준중량을 계산합니다.
                        실제 사용 중량이 기준중량 대비 ±10% 변하면 점수에 ±3점 반영됩니다.
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700 w-12">남성</label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            max="300"
                            className="w-24 h-8 text-center text-sm bg-white"
                            value={getBaseRatioPct(rule, 'm')}
                            onChange={(e) => updateBaseRatio(rule.rule_code, 'm', e.target.value)}
                            data-testid={`base-ratio-m-${rule.exercise}`}
                          />
                          <span className="text-sm text-gray-600">% (체중 대비)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700 w-12">여성</label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            max="300"
                            className="w-24 h-8 text-center text-sm bg-white"
                            value={getBaseRatioPct(rule, 'f')}
                            onChange={(e) => updateBaseRatio(rule.rule_code, 'f', e.target.value)}
                            data-testid={`base-ratio-f-${rule.exercise}`}
                          />
                          <span className="text-sm text-gray-600">% (체중 대비)</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        예: 남성 50% / 체중 70kg 회원 → 기준중량 35kg
                      </div>
                    </div>
                  )}

                  {/* Score map info */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium mb-2">점수 매핑</div>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {thresholdLevels.map(level => (
                        <span key={level} className="px-2 py-1 bg-white rounded border">
                          {level}: {rule.score_map?.[level] || 0}점
                        </span>
                      ))}
                      <span className="text-gray-500 text-xs self-center">
                        (구간 사이 선형 보간, 최대값 이상 선형 외삽)
                      </span>
                    </div>
                  </div>

                  {/* Thresholds table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 font-medium">성별</th>
                          <th className="text-left p-2 font-medium">연령대</th>
                          {thresholdLevels.map(level => (
                            <th key={level} className="text-center p-2 font-medium min-w-20">
                              {level}
                              <div className="text-xs font-normal text-gray-500">
                                ({rule.score_map?.[level] || 0}점)
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['M', 'F'].map(gender => (
                          ['20', '30', '40', '50'].map((ageBand, idx) => (
                            <tr 
                              key={`${gender}-${ageBand}`}
                              className={`border-b ${idx === 0 ? 'border-t-2' : ''}`}
                            >
                              {idx === 0 && (
                                <td 
                                  className="p-2 font-medium bg-gray-50"
                                  rowSpan={4}
                                >
                                  {GENDER_LABELS[gender]}
                                </td>
                              )}
                              <td className="p-2">{AGE_BAND_LABELS[ageBand]}</td>
                              {thresholdLevels.map(level => {
                                const currentVal = getThresholdValue(rule.rule_code, gender, ageBand, level);
                                // 로잉 퍼포먼스(seconds_inverse)는 mm:ss 미리보기 추가
                                const showMmss = rule.unit === 'seconds_inverse' && currentVal !== '' && currentVal !== null && !Number.isNaN(Number(currentVal));
                                const mmss = showMmss
                                  ? `${Math.floor(Number(currentVal) / 60)}:${String(Math.round(Number(currentVal) % 60)).padStart(2, '0')}`
                                  : null;
                                return (
                                  <td key={level} className="p-2">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      className="w-20 h-8 text-center text-sm"
                                      value={currentVal}
                                      onChange={(e) => updateThreshold(
                                        rule.rule_code, 
                                        gender, 
                                        ageBand, 
                                        level, 
                                        e.target.value
                                      )}
                                    />
                                    {mmss && (
                                      <div className="text-[10px] text-gray-400 text-center mt-0.5" data-testid={`mmss-${rule.rule_code}-${gender}-${ageBand}-${level}`}>
                                        {mmss}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Unit info */}
                  <div className="mt-4 text-xs text-gray-500">
                    <span className="font-medium">단위:</span>{' '}
                    {rule.unit === 'ratio' && '상대근력 (1RM / 체중)'}
                    {rule.unit === 'reps' && '반복 횟수 (3분 테스트)'}
                    {rule.unit === 'vo2' && 'VO2max (ml/kg/min)'}
                    {rule.unit === 'seconds_inverse' && '초 (낮을수록 좋음, mm:ss 미리보기 자동 표시)'}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {rules.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          등록된 기준표가 없습니다
        </div>
      )}
    </div>
  );
};

export default ScoringRules;
